import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectSong } from 'services/store/newSongs/selectors';
import { locations } from 'services/routes/locations';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import songsApi from 'services/api/songs/songsApi';
import { SongType } from 'services/api/entities';
import { events } from 'services/analytics';
import useMessageSnackbar from '../useMessageSnackbar';
import useAuthStore from '../auth';
import useAnalytics from '../analytics';

type PropsType = {
  songId: string;
  location: AnalyticsEventLocation;
};

const useAddToLibrary = ({ songId, location }: PropsType) => {
  const [addToLibrary, { isLoading, isError, isSuccess }] =
    songsApi.useAddToLibraryMutation();

  const song = useSelector(selectSong(songId));

  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;
  const navigate = useNavigate();
  const { displayErrorSnackbar, displaySuccessSnackbar } = useMessageSnackbar();
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const addSongToLibrary = useCallback(() => {
    if (!song) {
      return;
    }

    const { id, songType, isInLibrary } = song;
    if (!isLoggedIn) {
      navigate(locations.general.register.pathname(''));
    } else if (!isInLibrary && songType !== SongType.STUDIO) {
      addToLibrary({ songId: id });
      track(events.addedLibrary(id, songType, location));
    }
  }, [song, isLoggedIn, navigate, addToLibrary, track, location]);

  useEffect(() => {
    if (isSuccess && song) {
      displaySuccessSnackbar(
        `${t('songList:addToLibrarySuccess')} ${song.title}`,
        t('songList:goToLibrary'),
        () => navigate(locations.library.tracks.liked.pathname())
      );
    }
  }, [isSuccess, t, navigate, displaySuccessSnackbar, song]);

  useEffect(() => {
    if (isError) {
      displayErrorSnackbar(t('messages:error:unknown'));
    }
  }, [t, isError, displayErrorSnackbar]);

  return {
    isLoading,
    addSongToLibrary,
  };
};

export default useAddToLibrary;
